import { AppName, type CountryCode } from "../types/common";
import {
  CONFIG_CATALOG,
  type ICountryAppUrls,
  type IEnvironmentConfig,
  type IEnvironmentFlags,
} from "./catalog";
import {
  type AppEnvironment,
  DEPLOYED_ENVIRONMENTS,
  isAppEnvironment,
} from "./environment";
import { ConfigError } from "./errors";

export interface IAppConfig {
  readonly environment: AppEnvironment;
  /** environment is test, staging or production. */
  readonly isDeployed: boolean;
  readonly isProduction: boolean;
  /** staging + production only (not `test`), same as before KREA-4314. */
  readonly isSentryEnabled: boolean;
  /** Environment-derived switches from the catalog. */
  readonly flags: IEnvironmentFlags;
  readonly appName: AppName;
  /** `KREA_API_ROOT_URL` override ?? catalog. */
  readonly apiRootUrl: string;
  readonly lenderApiDocsUrl: string;
  /** Admin ChatKit chat endpoint, derived from `apiRootUrl` (so an override applies). */
  readonly chatkitChatEndpoint: string;
  readonly appUrls: (country: CountryCode) => ICountryAppUrls;
}

/** Pre-KREA-4314 names (Vite's default `VITE_` prefix). Fail loudly, never ignore. */
const LEGACY_ENV_NAMES: readonly (readonly [string, string])[] = [
  ["VITE_APP_NAME", "KREA_APP_NAME"],
  ["VITE_API_ROOT_URL", "KREA_API_ROOT_URL"],
];

const APP_NAMES: readonly string[] = Object.values(AppName);

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

/**
 * Validates an API root URL override (`KREA_API_ROOT_URL` in the Vite apps,
 * `NEXT_PUBLIC_KREA_API_ROOT_URL` in the websites). Pure, shared by both.
 * Accepted only in development and local-docker; throws `ConfigError`.
 */
export const parseApiRootUrlOverride = (
  value: string | undefined,
  environment: AppEnvironment,
  variableName = "KREA_API_ROOT_URL",
): string | undefined => {
  if (value === undefined || value === "") return undefined;

  if (DEPLOYED_ENVIRONMENTS.includes(environment)) {
    // Fail closed: a deployed bundle must never point at a backend that is
    // not in the catalog.
    throw new ConfigError(
      "CONFIG_OVERRIDE_IN_DEPLOYED_MODE",
      `${variableName} "${value}" is set in mode "${environment}". Overrides are only allowed in development and local-docker.`,
    );
  }

  let parsed: URL | undefined;
  try {
    parsed = new URL(value);
  } catch {
    parsed = undefined;
  }
  if (
    !parsed ||
    (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
    value.endsWith("/")
  ) {
    throw new ConfigError(
      "CONFIG_INVALID_URL",
      `${variableName} "${value}" (mode "${environment}") must be an absolute http(s) URL without a trailing slash`,
    );
  }
  return value;
};

/**
 * core-service base URL of an environment: the validated override when set,
 * else the catalog value. Shared by `parseAppConfig` and the websites.
 */
export const resolveApiRootUrl = (
  environment: AppEnvironment,
  rawOverride: string | undefined,
  variableName?: string,
): string =>
  parseApiRootUrlOverride(rawOverride, environment, variableName) ??
  CONFIG_CATALOG[environment].apiRootUrl;

/**
 * Pure: validates the raw Vite env (`MODE`, `KREA_*`) and resolves the config.
 * Throws `ConfigError`. Runs at build time (`assertAppEnv`) and at app boot.
 */
export const parseAppConfig = (raw: Record<string, unknown>): IAppConfig => {
  const mode = asString(raw.MODE);
  if (!isAppEnvironment(mode)) {
    throw new ConfigError(
      "CONFIG_UNKNOWN_ENVIRONMENT",
      `Vite mode "${String(raw.MODE)}" is not a known environment`,
    );
  }
  const environment: AppEnvironment = mode;

  for (const [legacy, current] of LEGACY_ENV_NAMES) {
    if (raw[legacy] !== undefined) {
      throw new ConfigError(
        "CONFIG_LEGACY_VITE_ENV",
        `${legacy} is no longer read (mode "${environment}"). Rename it to ${current}`,
      );
    }
  }

  const rawAppName = asString(raw.KREA_APP_NAME);
  if (rawAppName === undefined || rawAppName === "") {
    throw new ConfigError(
      "CONFIG_MISSING_APP_NAME",
      `KREA_APP_NAME is not set (mode "${environment}"). Set it in apps/<app>/.env`,
    );
  }
  if (!APP_NAMES.includes(rawAppName)) {
    throw new ConfigError(
      "CONFIG_INVALID_APP_NAME",
      `KREA_APP_NAME "${rawAppName}" (mode "${environment}") must be one of: ${APP_NAMES.join(", ")}`,
    );
  }

  const catalog: IEnvironmentConfig = CONFIG_CATALOG[environment];
  const appName = rawAppName as AppName;

  const apiRootUrl = resolveApiRootUrl(
    environment,
    asString(raw.KREA_API_ROOT_URL),
  );

  return {
    environment,
    isDeployed: DEPLOYED_ENVIRONMENTS.includes(environment),
    isProduction: environment === "production",
    isSentryEnabled: catalog.flags.sentryEnabled,
    flags: catalog.flags,
    appName,
    apiRootUrl,
    lenderApiDocsUrl: catalog.lenderApiDocsUrl,
    chatkitChatEndpoint: `${apiRootUrl}/api/v1/chatkit/chat`,
    appUrls: (country) => catalog.apps[country],
  };
};
